/** Last-known-good session, cached so an installed PWA can open offline without
 *  bouncing to an unreachable IdP.
 *
 *  The backend session cookie is HttpOnly, so the frontend cannot read it to
 *  decide "is a session present". Instead we cache the userinfo from the last
 *  successful /auth/me and stamp it with an expiry that MIRRORS the backend
 *  SessionMiddleware max_age. This is an approximation: if the real cookie has
 *  expired server-side while our hint has not, the first online /api call
 *  returns 401 and the client bounces to login — which is correct. */
import type { User } from './useAuth';

const KEY = 'sudoku:auth:user';
// Mirrors backend SessionMiddleware max_age (app/auth.py). Keep the two in sync.
const TTL_MS = 30 * 24 * 60 * 60 * 1000;

interface CachedAuth {
  user: User;
  expiresAt: number;
}

export function cacheUser(user: User): void {
  try {
    const entry: CachedAuth = { user, expiresAt: Date.now() + TTL_MS };
    localStorage.setItem(KEY, JSON.stringify(entry));
  } catch {
    // Storage full / disabled — non-fatal; offline access simply won't persist.
  }
}

export function loadCachedUser(): User | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const { user, expiresAt } = JSON.parse(raw) as CachedAuth;
    if (Date.now() > expiresAt) {
      localStorage.removeItem(KEY);
      return null;
    }
    return user;
  } catch {
    return null;
  }
}

export function clearCachedUser(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
}
